/**
 * The two formatters the Overview's own cards share, and nothing else.
 *
 * These lived at the top of a 2,204-line `HomePage.tsx` until slice 20 split
 * that file into the dashboard it describes. They are here rather than in
 * `lib/format.ts` on purpose: `formatCount`, `formatDate` and `formatDateTime`
 * are the product's, used on twenty screens, and both of these are arguments
 * about *this* screen that would be wrong somewhere else.
 *
 * A third candidate was rejected — a `formatDay` for the chart axis. The axis
 * is UTC and every other date on this page is rendered in the reader's own
 * zone, so one function used by both would be one function telling two
 * different truths about what a date means. The chart formats its own days,
 * beside its own axis label, in the file that draws them.
 */
import { pluralise } from '../../lib/format';

/**
 * A rate, as a percentage, at a precision that can actually resolve the line it
 * is drawn against.
 *
 * The complaint threshold is 0.1%. Rounded to one decimal place, a workspace at
 * 0.14% — forty percent over the AWS review line — prints as "0.1%" and looks
 * exactly like one sitting on it. So anything under 1% gets two decimal places
 * and the bigger bounce numbers get one; the alternative, a single format for
 * both, has to be wrong for one of them.
 */
export function formatRate(value: number): string {
  const small = value < 0.01;
  return new Intl.NumberFormat(undefined, {
    style: 'percent',
    minimumFractionDigits: small ? 2 : 1,
    maximumFractionDigits: small ? 2 : 1,
  }).format(value);
}

const MINUTE = 60 * 1_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * How long ago, in words, rounded down.
 *
 * Rounded down and never up: a check taken 23 hours ago is "23 hours ago" and
 * not "a day ago", because the whole point of printing an age is that the
 * reader decides for themselves whether to trust the figure under it. Rounding
 * in the flattering direction is a small lie in the one place this screen has
 * promised not to tell one.
 */
export function formatAge(ms: number): string {
  if (ms < MINUTE) return 'just now';
  if (ms < HOUR) return `${pluralise(Math.floor(ms / MINUTE), 'minute')} ago`;
  if (ms < DAY) return `${pluralise(Math.floor(ms / HOUR), 'hour')} ago`;
  return `${pluralise(Math.floor(ms / DAY), 'day')} ago`;
}
